import {
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
  orderBy,
  getDoc,
} from 'firebase/firestore';
import { db } from '../../../config';
import { MasterMappingType, MasterType, MasterWithMappingType } from '../../../../../types/master';
import { masterStatus } from '../../../../../constants/enum';

export const getMasterMappings = async (masterId: string): Promise<MasterMappingType[]> => {
  const mappingsRef = collection(db, 'masters', masterId, 'master_mappings');
  const q = query(mappingsRef, orderBy('created_at', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(d => ({
    mapping_id: d.id,
    master_id: masterId,
    ...d.data(),
  })) as MasterMappingType[];
};

// 全マスタの mapping を一括取得（管理画面用）
export const getAllMasterMappingsByCompanyId = async (
  companyId: string,
): Promise<MasterMappingType[]> => {
  const mappingsRef = collectionGroup(db, 'master_mappings');
  const snapshot = await getDocs(mappingsRef);
  const results: MasterMappingType[] = [];

  for (const d of snapshot.docs) {
    const masterRef = d.ref.parent.parent;
    if (!masterRef) continue;
    // company_id フィルタは親 master 側で行う必要があるため、呼び出し元で master 一覧と突き合わせる
    results.push({
      mapping_id: d.id,
      master_id: masterRef.id,
      ...d.data(),
    } as MasterMappingType);
    // companyId は引数に取るが、最終フィルタは呼び出し元で行う想定
    void companyId;
  }

  return results;
};

// パターン単位で、紐づくマスタ+マッピングをまとめて取得（参照側用）
export const getMasterMappingsByPatternId = async (
  companyId: string,
  patternId: string,
): Promise<MasterWithMappingType[]> => {
  const mappingsRef = collectionGroup(db, 'master_mappings');
  const q = query(mappingsRef, where('pattern_id', '==', patternId));
  const snapshot = await getDocs(q);

  const results: MasterWithMappingType[] = [];
  for (const d of snapshot.docs) {
    const masterRef = d.ref.parent.parent;
    if (!masterRef) continue;
    const masterSnap = await getDoc(masterRef);
    if (!masterSnap.exists()) continue;
    const masterData = masterSnap.data() as Omit<MasterType, 'master_id'>;
    if (masterData.company_id !== companyId) continue;
    // 論理削除済み（#2896）の master / mapping は照合・表示の対象外。
    if (masterData.status === masterStatus.deleted) continue;

    const mappingData = d.data() as Omit<MasterMappingType, 'mapping_id' | 'master_id'> & {
      status?: number;
    };
    if (mappingData.status === masterStatus.deleted) continue;
    results.push({
      master_id: masterRef.id,
      mapping_id: d.id,
      company_id: masterData.company_id,
      file_name: masterData.file_name,
      file_path: masterData.file_path,
      file_size: masterData.file_size,
      content_type: masterData.content_type,
      row_count: masterData.row_count,
      csv_columns: masterData.csv_columns,
      uploaded_by_uid: masterData.uploaded_by_uid,
      uploaded_by_name: masterData.uploaded_by_name,
      uploaded_by_icon: masterData.uploaded_by_icon,
      created_at: mappingData.created_at,
      updated_at: mappingData.updated_at,
      pattern_id: mappingData.pattern_id,
      pattern_name: mappingData.pattern_name,
      search_item_id: mappingData.search_item_id,
      search_item_label: mappingData.search_item_label,
      search_csv_column: mappingData.search_csv_column,
      column_mappings: mappingData.column_mappings ?? [],
      scope_item_id: mappingData.scope_item_id,
      scope_item_label: mappingData.scope_item_label,
      // 照合方式（#3022）。再照合モーダルの初期タブ判定・部分一致の照合先列に使う。
      match_mode: mappingData.match_mode,
      // 検索キー・絞り込みキー・実行順（#3180）。
      // 旧データでは未設定のため、参照側は resolveSearchKey / resolveFilterKeys を通すこと。
      search_key: mappingData.search_key,
      filter_keys: mappingData.filter_keys,
      execution_order: mappingData.execution_order,
      // 自動反映の下限スコア（#3263）。未設定ならバックエンドの既定値が使われる。
      auto_apply_score: mappingData.auto_apply_score,
      // 要注意にする下限スコア（#3309）。未設定ならバックエンドの既定値が使われる。
      review_threshold: mappingData.review_threshold,
      // AI照合リランク（#3355）。未設定は OFF 扱い。
      enable_llm_rerank: mappingData.enable_llm_rerank,
    });
  }

  return results;
};
